/**
 * Constraint solver wrapper using Kiwi.js
 *
 * Provides a simplified API for creating and solving layout constraints
 * using the Cassowary-style constraint solver.
 */

import * as kiwi from 'kiwi.js';
import type { NodeId } from '@core/types/common';

/**
 * Constraint strength levels
 */
export type ConstraintStrength = 'required' | 'strong' | 'medium' | 'weak';

/**
 * Maps our strength levels to Kiwi strengths
 */
const STRENGTH_MAP: Record<ConstraintStrength, number> = {
  required: kiwi.Strength.required,
  strong: kiwi.Strength.strong,
  medium: kiwi.Strength.medium,
  weak: kiwi.Strength.weak,
};

/**
 * Variable representing a layout property
 */
export interface LayoutVariable {
  readonly nodeId: NodeId;
  readonly property: 'x' | 'y' | 'width' | 'height';
  readonly variable: kiwi.Variable;
}

/**
 * Constraint definition
 */
export interface ConstraintDef {
  readonly id: string;
  readonly expression: kiwi.Expression;
  readonly operator: 'eq' | 'le' | 'ge';
  readonly strength: ConstraintStrength;
}

/**
 * Node layout result
 */
export interface LayoutResult {
  readonly nodeId: NodeId;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Constraint solver - wraps Kiwi.js for layout calculations
 */
export class ConstraintSolver {
  private solver: kiwi.Solver;
  private variables: Map<string, LayoutVariable> = new Map();
  private constraints: Map<string, kiwi.Constraint> = new Map();

  constructor() {
    this.solver = new kiwi.Solver();
  }

  // =========================================================================
  // Variable Management
  // =========================================================================

  /**
   * Get or create a variable for a node property.
   */
  getVariable(nodeId: NodeId, property: 'x' | 'y' | 'width' | 'height'): kiwi.Variable {
    const key = `${nodeId}:${property}`;
    let layoutVar = this.variables.get(key);

    if (!layoutVar) {
      const variable = new kiwi.Variable(key);
      layoutVar = { nodeId, property, variable };
      this.variables.set(key, layoutVar);
    }

    return layoutVar.variable;
  }

  /**
   * Get the X variable for a node.
   */
  x(nodeId: NodeId): kiwi.Variable {
    return this.getVariable(nodeId, 'x');
  }

  /**
   * Get the Y variable for a node.
   */
  y(nodeId: NodeId): kiwi.Variable {
    return this.getVariable(nodeId, 'y');
  }

  /**
   * Get the width variable for a node.
   */
  width(nodeId: NodeId): kiwi.Variable {
    return this.getVariable(nodeId, 'width');
  }

  /**
   * Get the height variable for a node.
   */
  height(nodeId: NodeId): kiwi.Variable {
    return this.getVariable(nodeId, 'height');
  }

  /**
   * Get the right edge (x + width) expression for a node.
   */
  right(nodeId: NodeId): kiwi.Expression {
    return new kiwi.Expression(this.x(nodeId), this.width(nodeId));
  }

  /**
   * Get the bottom edge (y + height) expression for a node.
   */
  bottom(nodeId: NodeId): kiwi.Expression {
    return new kiwi.Expression(this.y(nodeId), this.height(nodeId));
  }

  /**
   * Get the center X (x + width/2) expression for a node.
   */
  centerX(nodeId: NodeId): kiwi.Expression {
    return new kiwi.Expression(
      this.x(nodeId),
      [0.5, this.width(nodeId)]
    );
  }

  /**
   * Get the center Y (y + height/2) expression for a node.
   */
  centerY(nodeId: NodeId): kiwi.Expression {
    return new kiwi.Expression(
      this.y(nodeId),
      [0.5, this.height(nodeId)]
    );
  }

  // =========================================================================
  // Constraint Management
  // =========================================================================

  /**
   * Add an equality constraint: expression == 0
   */
  addEquality(
    id: string,
    expression: kiwi.Expression,
    strength: ConstraintStrength = 'required'
  ): void {
    const constraint = new kiwi.Constraint(
      expression,
      kiwi.Operator.Eq,
      STRENGTH_MAP[strength]
    );
    this.addConstraintInternal(id, constraint);
  }

  /**
   * Add a less-than-or-equal constraint: expression <= 0
   */
  addLessOrEqual(
    id: string,
    expression: kiwi.Expression,
    strength: ConstraintStrength = 'required'
  ): void {
    const constraint = new kiwi.Constraint(
      expression,
      kiwi.Operator.Le,
      STRENGTH_MAP[strength]
    );
    this.addConstraintInternal(id, constraint);
  }

  /**
   * Add a greater-than-or-equal constraint: expression >= 0
   */
  addGreaterOrEqual(
    id: string,
    expression: kiwi.Expression,
    strength: ConstraintStrength = 'required'
  ): void {
    const constraint = new kiwi.Constraint(
      expression,
      kiwi.Operator.Ge,
      STRENGTH_MAP[strength]
    );
    this.addConstraintInternal(id, constraint);
  }

  /**
   * Add a constraint that a variable equals a constant.
   */
  addValueConstraint(
    id: string,
    variable: kiwi.Variable,
    value: number,
    strength: ConstraintStrength = 'required'
  ): void {
    // expression: variable - value == 0
    const expression = new kiwi.Expression(variable, -value);
    this.addEquality(id, expression, strength);
  }

  /**
   * Add a constraint that variable1 == variable2 + offset.
   */
  addRelationConstraint(
    id: string,
    variable1: kiwi.Variable,
    variable2: kiwi.Variable,
    offset: number = 0,
    strength: ConstraintStrength = 'required'
  ): void {
    // expression: variable1 - variable2 - offset == 0
    const expression = new kiwi.Expression(
      variable1,
      [-1, variable2],
      -offset
    );
    this.addEquality(id, expression, strength);
  }

  /**
   * Remove a constraint by ID.
   */
  removeConstraint(id: string): void {
    const constraint = this.constraints.get(id);
    if (constraint) {
      try {
        this.solver.removeConstraint(constraint);
      } catch {
        // Constraint may not be in solver
      }
      this.constraints.delete(id);
    }
  }

  /**
   * Check if a constraint exists.
   */
  hasConstraint(id: string): boolean {
    return this.constraints.has(id);
  }

  /**
   * Internal method to add a constraint.
   */
  private addConstraintInternal(id: string, constraint: kiwi.Constraint): void {
    // Remove existing constraint with same ID
    this.removeConstraint(id);

    try {
      this.constraints.set(id, constraint);
      this.solver.addConstraint(constraint);
    } catch (e) {
      // Constraint may be unsatisfiable - remove it and continue
      this.constraints.delete(id);
      console.warn(`Unsatisfiable constraint: ${id}`, e);
    }
  }

  // =========================================================================
  // Edit Variables (for dragging)
  // =========================================================================

  /**
   * Suggest a value for a variable (for interactive editing).
   */
  suggestValue(variable: kiwi.Variable, value: number): void {
    try {
      if (!this.solver.hasEditVariable(variable)) {
        this.solver.addEditVariable(variable, kiwi.Strength.strong);
      }
      this.solver.suggestValue(variable, value);
    } catch {
      // Variable may not be editable
    }
  }

  /**
   * Remove an edit variable.
   */
  removeEditVariable(variable: kiwi.Variable): void {
    try {
      if (this.solver.hasEditVariable(variable)) {
        this.solver.removeEditVariable(variable);
      }
    } catch {
      // Variable may not exist
    }
  }

  // =========================================================================
  // Solving
  // =========================================================================

  /**
   * Update the solver and calculate new values.
   */
  solve(): void {
    this.solver.updateVariables();
  }

  /**
   * Get the solved value for a variable.
   */
  getValue(variable: kiwi.Variable): number {
    return variable.value();
  }

  /**
   * Get the layout result for a node.
   */
  getNodeLayout(nodeId: NodeId): LayoutResult {
    return {
      nodeId,
      x: this.x(nodeId).value(),
      y: this.y(nodeId).value(),
      width: this.width(nodeId).value(),
      height: this.height(nodeId).value(),
    };
  }

  /**
   * Get layout results for all nodes with variables.
   */
  getAllLayouts(): Map<NodeId, LayoutResult> {
    const nodeIds = new Set<NodeId>();
    for (const layoutVar of this.variables.values()) {
      nodeIds.add(layoutVar.nodeId);
    }

    const results = new Map<NodeId, LayoutResult>();
    for (const nodeId of nodeIds) {
      results.set(nodeId, this.getNodeLayout(nodeId));
    }

    return results;
  }

  // =========================================================================
  // Utility Methods
  // =========================================================================

  /**
   * Clear all constraints and variables.
   */
  clear(): void {
    // Remove all constraints
    for (const constraint of this.constraints.values()) {
      try {
        this.solver.removeConstraint(constraint);
      } catch {
        // Ignore errors
      }
    }

    this.constraints.clear();
    this.variables.clear();
  }

  /**
   * Reset the solver completely.
   */
  reset(): void {
    this.clear();
    this.solver = new kiwi.Solver();
  }

  /**
   * Get the number of constraints.
   */
  get constraintCount(): number {
    return this.constraints.size;
  }

  /**
   * Get the number of variables.
   */
  get variableCount(): number {
    return this.variables.size;
  }
}

/**
 * Create a new constraint solver.
 */
export function createConstraintSolver(): ConstraintSolver {
  return new ConstraintSolver();
}

// ============================================================================
// Helper Functions for Common Constraints
// ============================================================================

/**
 * Create constraints to contain a child within a parent with padding.
 */
export function createContainmentConstraints(
  solver: ConstraintSolver,
  parentId: NodeId,
  childId: NodeId,
  padding: { top: number; right: number; bottom: number; left: number }
): void {
  const prefix = `contain:${parentId}:${childId}`;

  // Child left >= parent left + padding
  solver.addGreaterOrEqual(
    `${prefix}:left`,
    new kiwi.Expression(
      solver.x(childId),
      [-1, solver.x(parentId)],
      -padding.left
    )
  );

  // Child top >= parent top + padding
  solver.addGreaterOrEqual(
    `${prefix}:top`,
    new kiwi.Expression(
      solver.y(childId),
      [-1, solver.y(parentId)],
      -padding.top
    )
  );

  // Child right <= parent right - padding
  solver.addLessOrEqual(
    `${prefix}:right`,
    new kiwi.Expression(
      solver.right(childId),
      new kiwi.Expression([-1, solver.right(parentId)]),
      padding.right
    )
  );

  // Child bottom <= parent bottom - padding
  solver.addLessOrEqual(
    `${prefix}:bottom`,
    new kiwi.Expression(
      solver.bottom(childId),
      new kiwi.Expression([-1, solver.bottom(parentId)]),
      padding.bottom
    )
  );
}

/**
 * Create constraints to align a child within a parent.
 */
export function createAlignmentConstraints(
  solver: ConstraintSolver,
  parentId: NodeId,
  childId: NodeId,
  horizontalAlign: 'left' | 'center' | 'right' | 'stretch',
  verticalAlign: 'top' | 'center' | 'bottom' | 'stretch',
  strength: ConstraintStrength = 'medium'
): void {
  const prefix = `align:${parentId}:${childId}`;

  // Horizontal alignment
  switch (horizontalAlign) {
    case 'left':
      solver.addRelationConstraint(
        `${prefix}:h`,
        solver.x(childId),
        solver.x(parentId),
        0,
        strength
      );
      break;
    case 'center':
      solver.addEquality(
        `${prefix}:h`,
        new kiwi.Expression(
          solver.centerX(childId),
          new kiwi.Expression([-1], solver.centerX(parentId))
        ),
        strength
      );
      break;
    case 'right':
      solver.addEquality(
        `${prefix}:h`,
        new kiwi.Expression(
          solver.right(childId),
          new kiwi.Expression([-1], solver.right(parentId))
        ),
        strength
      );
      break;
    case 'stretch':
      solver.addRelationConstraint(
        `${prefix}:h:left`,
        solver.x(childId),
        solver.x(parentId),
        0,
        strength
      );
      solver.addEquality(
        `${prefix}:h:width`,
        new kiwi.Expression(
          solver.width(childId),
          [-1, solver.width(parentId)]
        ),
        strength
      );
      break;
  }

  // Vertical alignment
  switch (verticalAlign) {
    case 'top':
      solver.addRelationConstraint(
        `${prefix}:v`,
        solver.y(childId),
        solver.y(parentId),
        0,
        strength
      );
      break;
    case 'center':
      solver.addEquality(
        `${prefix}:v`,
        new kiwi.Expression(
          solver.centerY(childId),
          new kiwi.Expression([-1], solver.centerY(parentId))
        ),
        strength
      );
      break;
    case 'bottom':
      solver.addEquality(
        `${prefix}:v`,
        new kiwi.Expression(
          solver.bottom(childId),
          new kiwi.Expression([-1], solver.bottom(parentId))
        ),
        strength
      );
      break;
    case 'stretch':
      solver.addRelationConstraint(
        `${prefix}:v:top`,
        solver.y(childId),
        solver.y(parentId),
        0,
        strength
      );
      solver.addEquality(
        `${prefix}:v:height`,
        new kiwi.Expression(
          solver.height(childId),
          [-1, solver.height(parentId)]
        ),
        strength
      );
      break;
  }
}

/**
 * Create constraints for fixed dimensions.
 */
export function createSizeConstraints(
  solver: ConstraintSolver,
  nodeId: NodeId,
  size: Partial<{ width: number; height: number }>,
  strength: ConstraintStrength = 'required'
): void {
  const prefix = `size:${nodeId}`;

  if (size.width !== undefined) {
    solver.addValueConstraint(`${prefix}:w`, solver.width(nodeId), size.width, strength);
  }

  if (size.height !== undefined) {
    solver.addValueConstraint(`${prefix}:h`, solver.height(nodeId), size.height, strength);
  }
}

/**
 * Create constraints for fixed position.
 */
export function createPositionConstraints(
  solver: ConstraintSolver,
  nodeId: NodeId,
  position: Partial<{ x: number; y: number }>,
  strength: ConstraintStrength = 'required'
): void {
  const prefix = `pos:${nodeId}`;

  if (position.x !== undefined) {
    solver.addValueConstraint(`${prefix}:x`, solver.x(nodeId), position.x, strength);
  }

  if (position.y !== undefined) {
    solver.addValueConstraint(`${prefix}:y`, solver.y(nodeId), position.y, strength);
  }
}
